// frontend/src/pages/ReceptionChecklist.js
// Vehicle reception checklist — chrome/ink, French. Grouped into panels so the
// technician fills it top to bottom without hunting for fields.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import SearchableSelect from '../components/SearchableSelect';
import VehicleIdentityCard from '../components/VehicleIdentityCard';
import {
  Page, Panel, Button, Field, Checkbox, ErrorNote, Note,
  inputClass, selectClass, textareaClass,
} from '../components/ui/Iparc';

const EMPTY = {
  vehicleId: '',
  fuelLevel: 50,
  odometer: '',
  glassBroken: false,
  scratchesOrDents: '',
  sideMirrorDamage: false,
  headlightDamage: false,
  tireBoltIssue: false,
  tireCondition: 'Good',
  dashboardWarningLights: '',
  customerRemarks: '',
  technicianComments: '',
  exteriorCondition: 'Good',
  interiorCondition: 'Normal',
  numberOfKeys: 1,
  personalItemsLeft: '',
};

// Values stay in English — they're what the backend stores. Only labels are FR.
const EXTERIOR = [
  ['Excellent', 'Excellent'], ['Good', 'Bon'], ['Fair', 'Moyen'], ['Poor', 'Mauvais'],
];
const INTERIOR = [['Clean', 'Propre'], ['Normal', 'Normal'], ['Dirty', 'Sale']];
const TIRES = [['Good', 'Bon'], ['Worn', 'Usé'], ['Damaged', 'Endommagé']];

const DAMAGE = [
  ['glassBroken', 'Vitre brisée'],
  ['sideMirrorDamage', 'Rétroviseur endommagé'],
  ['headlightDamage', 'Phare endommagé'],
  ['tireBoltIssue', 'Boulons de roue'],
];

const ReceptionChecklist = () => {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await axios.get(`${API_URL}/vehicles/assigned`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setVehicles(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch vehicles', err);
      }
    };
    if (token) fetchVehicles();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setOkMsg('');
    if (!form.vehicleId) return setError('Sélectionnez un véhicule.');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/checklist`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOkMsg('Fiche de réception enregistrée.');
      setForm(EMPTY);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || "Échec de l'enregistrement de la fiche.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Fiche de réception" subtitle="Atelier" width="narrow">
      {error && <ErrorNote>{error}</ErrorNote>}
      {okMsg && <Note tone="ok">{okMsg}</Note>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        <Panel title="Véhicule">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <Field label="Véhicule" required className="sm:col-span-3">
              <SearchableSelect
                options={vehicles.map((v) => ({
                  value: v._id,
                  label: `${v.plateNumber} — ${v.brand || v.make || ''} ${v.model || ''}`.trim(),
                  sublabel: v.vin ? `VIN ${String(v.vin).toUpperCase()}` : undefined,
                }))}
                value={form.vehicleId}
                onChange={(val) => handleChange({ target: { name: 'vehicleId', value: val, type: 'text' } })}
                placeholder="Sélectionner un véhicule"
                name="vehicleId"
                required
              />
            </Field>
            {/* Reception is where the VIN on the car is checked against the
                VIN on file, so it has to be readable at that moment. */}
            {form.vehicleId && (
              <VehicleIdentityCard
                className="sm:col-span-3"
                label="Véhicule reçu"
                vehicle={vehicles.find((v) => v._id === form.vehicleId)}
              />
            )}
            <Field label="Niveau de carburant (%)">
              <input type="number" name="fuelLevel" value={form.fuelLevel} onChange={handleChange}
                     className={inputClass} min="0" max="100" required />
            </Field>
            <Field label="Kilométrage (km)">
              <input type="number" name="odometer" value={form.odometer} onChange={handleChange}
                     className={inputClass} placeholder="Ex : 84500" />
            </Field>
            <Field label="Nombre de clés">
              <input type="number" name="numberOfKeys" value={form.numberOfKeys} onChange={handleChange}
                     className={inputClass} min="0" max="5" />
            </Field>
          </div>
        </Panel>

        <Panel title="État à la réception">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <Field label="Extérieur">
              <select name="exteriorCondition" value={form.exteriorCondition} onChange={handleChange} className={selectClass}>
                {EXTERIOR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Intérieur">
              <select name="interiorCondition" value={form.interiorCondition} onChange={handleChange} className={selectClass}>
                {INTERIOR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Pneus">
              <select name="tireCondition" value={form.tireCondition} onChange={handleChange} className={selectClass}>
                {TIRES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>

          <div className="mt-4 pt-4 border-t border-[#241F1B]">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-3">
              Dommages constatés
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {DAMAGE.map(([name, label]) => (
                <Checkbox
                  key={name}
                  name={name}
                  label={label}
                  checked={form[name]}
                  onChange={handleChange}
                />
              ))}
            </div>
          </div>
        </Panel>

        <Panel title="Observations">
          <div className="flex flex-col gap-3.5">
            <Field label="Objets personnels laissés dans le véhicule">
              <input type="text" name="personalItemsLeft" value={form.personalItemsLeft} onChange={handleChange}
                     className={inputClass} placeholder="Ex : chargeur, lunettes de soleil" />
            </Field>
            <Field label="Rayures ou bosses">
              <textarea name="scratchesOrDents" value={form.scratchesOrDents} onChange={handleChange}
                        rows={2} className={textareaClass} placeholder="Décrivez les dommages visibles…" />
            </Field>
            <Field label="Voyants au tableau de bord">
              <textarea name="dashboardWarningLights" value={form.dashboardWarningLights} onChange={handleChange}
                        rows={2} className={textareaClass} placeholder="Ex : voyant moteur allumé" />
            </Field>
            <Field label="Remarques du client">
              <textarea name="customerRemarks" value={form.customerRemarks} onChange={handleChange}
                        rows={2} className={textareaClass} />
            </Field>
            <Field label="Commentaires du technicien">
              <textarea name="technicianComments" value={form.technicianComments} onChange={handleChange}
                        rows={2} className={textareaClass} />
            </Field>
          </div>
        </Panel>

        <div>
          <Button as="button" type="submit" variant="primary" disabled={saving} className="w-full sm:w-auto">
            {saving ? 'Enregistrement…' : 'Enregistrer la fiche'}
          </Button>
        </div>
      </form>
    </Page>
  );
};

export default ReceptionChecklist;
